import { TenantSettings, User, UserPermissions, UserType } from '@centecomm/portal-service';
import { useCurrentUser } from '@centecomm/portal-service-react';
import { LDClient } from 'launchdarkly-react-client-sdk';
import { UserTypeAgnosticSettings } from 'State/Settings/Types/UserTypeAgnosticSettings';
import useCheckIfCurrentCompanyIsPECO from 'utils/Hooks/CompanySpecific/PECO/useCheckIfCurrentCompanyIsPECO';

import { FeatureConfiguration } from '../Types/FeatureConfiguration';
import { FeatureVisibilityContext } from '../Types/FeatureVisibilityContext';
import { NavigationItem } from '../Types/NavigationItem';
import { NavigationLink } from '../Types/NavigationLink';
import { passesVisibilityRule } from './featureVisibility';

const isNavigationItem = (navigationItem: NavigationItem | undefined): navigationItem is NavigationItem =>
    typeof navigationItem !== 'undefined';

const isNavigationLink = (link: { text: string | undefined; url: string | undefined }): link is NavigationLink =>
    !!link.text && link.text.length > 0 && !!link.url && link.url.length > 0;

const filterNavigationLinks = (
    featureConfiguration: FeatureConfiguration[],
    checkPermission: (permission: UserPermissions) => boolean,
    currentUser: User,
    visibilityContext: FeatureVisibilityContext,
    settings?: UserTypeAgnosticSettings,
    ldClient?: LDClient,
): NavigationLink[] =>
    featureConfiguration
        .filter((feature: FeatureConfiguration) => !feature.disableFeature.includes('main'))
        .filter(({ navigationItem, checkIfEnabledViaSettings, launchDarklyFlags, visibilityRule }: FeatureConfiguration) => {
            const { isCurrentCompanyDetermined, idMatch: isPECOCurrentCompany } = useCheckIfCurrentCompanyIsPECO();
            const { hasSitePermission } = useCurrentUser();

            if (!navigationItem) return false;

            // Check LaunchDarkly flags if any are specified
            if (launchDarklyFlags && launchDarklyFlags.length > 0 && ldClient) {
                const allFlagsEnabled = launchDarklyFlags.every((flag) => ldClient.variation(flag, false) === true);

                if (!allFlagsEnabled) {
                    return false;
                }
            }

            if (!passesVisibilityRule(visibilityRule, visibilityContext)) {
                return false;
            }

            if (checkIfEnabledViaSettings && settings && !checkIfEnabledViaSettings(settings)) {
                return false;
            }

            const roles = currentUser.ownerOperator?.roles?.map((role) => role.name) || [];

            // IDT-1351 if the user company is peco, and the only role they have is Basic, then hide the following links
            // TODO: This is temporary until we add configuration to govern this
            if (
                isCurrentCompanyDetermined &&
                isPECOCurrentCompany &&
                roles.length === 1 &&
                roles.includes('Basic') &&
                currentUser.userType === UserType.OwnerOperator &&
                ['Documents', 'Sales', 'Insurance', 'Administration'].includes(navigationItem.route.text)
            ) {
                return false;
            }

            // filters out the invoices link if the feature is disabled for tenant users
            if (
                navigationItem.route.text === 'Invoices' &&
                currentUser.userType === UserType.Tenant &&
                !(settings as TenantSettings)?.invoice?.visible
            ) {
                return false;
            }

            // filter out Administration to only OO users who are admin and have site wide permission
            if (
                navigationItem.route.text === 'Administration' &&
                !hasSitePermission(UserPermissions.ManageAdminConfig) &&
                currentUser.userType === UserType.OwnerOperator &&
                !roles.includes('Admin')
            ) {
                return false;
            }

            const { permissions = [], isGloballyAccessible = false, isAllowed } = navigationItem;

            if (isGloballyAccessible) return true;

            // Route configs can have either `permissions: []` or `isAllowed(user)`, OR BOTH
            // So we check first if there's this extra validator so that we can combine its result
            // with `permissions`.
            const hasIsAllowedCheck = !!isAllowed;

            // (!) Repeating logic below instead of using `hasIsAllowedCheck` because of linter issues
            const isUserAllowed = hasIsAllowedCheck && isAllowed(currentUser);

            if (permissions && permissions.length > 0) {
                const currentUserHasPermissions = permissions.some((permission: UserPermissions) =>
                    checkPermission(permission),
                );

                // If the route has the `isAllowed` check, use that alongside the individual perms.
                // Otherwise default to standard permission check.
                return hasIsAllowedCheck ? currentUserHasPermissions && isUserAllowed : currentUserHasPermissions;
            }

            return isUserAllowed;
        })
        .map(({ navigationItem }) => navigationItem)
        .filter(isNavigationItem)
        .map(({ route }) => ({
            Icon: route.Icon,
            text: route.text || '',
            url: route.url || '',
            badgeText: route.badgeText,
            childItems: route?.childItems as NavigationLink[] | undefined,
        }))
        .filter(isNavigationLink);

export default filterNavigationLinks;
